import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../api';
import IsoHero from '../components/iso/IsoHero';
import IsoProcess from '../components/iso/IsoProcess';
import HomeGallery from './gallery/HomeGallery';
import { IsoGlyph, IsoIllustration } from '../components/iso/IsoSvg';
import { Icon, StatusBadge } from '../components/ui';
import { formatThickness } from '../utils/format';
import './home.css';
import usePageTitle from '../hooks/usePageTitle';

const SERVICES = [
  {
    glyph: 'laser',
    kicker: 'Laser cutting',
    title: 'Fibre laser, N₂ or O₂ assist',
    desc: 'Stainless to 25 mm, mild steel to 25 mm, aluminium to 12 mm. Clean, square edges with ±0.1 mm tolerance.',
    to: '/materials',
    cta: 'Materials & thicknesses'
  },
  {
    glyph: 'upload',
    kicker: 'Instant quote',
    title: 'Price from your DXF, in seconds',
    desc: 'We read cut length, pierce count and bounding box from the file. Pick a grade and thickness — the price updates live.',
    to: '/quote',
    cta: 'Request a quote'
  },
  {
    glyph: 'truck',
    kicker: 'Docket & dispatch',
    title: 'Delivered with the paperwork',
    desc: 'Mill certificates and compliance documents ride with every docket. Track dispatch and download them anytime.',
    to: '/mypage/certificates',
    cta: 'See the customer portal'
  }
];

const PORTAL_FEATURES = [
  { glyph: 'doc', t: 'Quotes', d: 'Every DXF you submit, priced and versioned.' },
  { glyph: 'stack', t: 'Orders', d: 'Live status from confirmation to delivery.' },
  { glyph: 'nest', t: 'Nesting reports', d: 'Sheet utilisation and offcuts per material.' },
  { glyph: 'cert', t: 'Certificates', d: 'Mill certs searchable by docket number.' }
];

export default function HomePage() {
  usePageTitle();
  const { data: materials = [] } = useQuery({
    queryKey: ['materials'],
    queryFn: () => api.materials.list(),
    staleTime: 5 * 60 * 1000
  });

  const grades = useMemo(() => {
    const map = new Map();
    materials.forEach(m => {
      const g = map.get(m.name) || { name: m.name, category: m.category, min: Infinity, max: -Infinity, count: 0, lead: m.leadTime };
      g.min = Math.min(g.min, m.thickness);
      g.max = Math.max(g.max, m.thickness);
      g.count += 1;
      map.set(m.name, g);
    });
    return Array.from(map.values()).slice(0, 4);
  }, [materials]);

  return (
    <div className="home">
      <IsoHero />

      {/* ── 서비스 ── */}
      <section className="sec home-svc">
        <div className="wrap">
          <div className="sec-head">
            <div className="kicker">What we do</div>
            <h2 className="sec-title">Drawing in, parts out — with the certificates.</h2>
            <p className="sec-lede">
              One place to quote, order and collect the paperwork for laser-cut sheet metal. Built for fabricators,
              engineers and makers who would rather not chase a phone call.
            </p>
          </div>

          <div className="svc-grid">
            {SERVICES.map((s, i) => (
              <Link key={s.kicker} to={s.to} className={`svc card card--iso iso-lift rise rise-${i + 1}`}>
                <div className="svc-stage">
                  <div className="iso-grid-bg" />
                  <IsoGlyph name={s.glyph} size={168} />
                  <span className="iso-sweep" aria-hidden="true" />
                </div>
                <div className="svc-body">
                  <div className="kicker kicker--plain">{s.kicker}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <span className="btn-link">
                    {s.cta}
                    <Icon name="arrowRight" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <IsoProcess />

      {/* ── 소재 ── */}
      <section className="sec home-mat">
        <div className="wrap home-mat-grid">
          <div>
            <div className="sec-head" style={{ marginBottom: 28 }}>
              <div className="kicker">Stock materials</div>
              <h2 className="sec-title">Stainless, mild steel, aluminium — on the floor, not on order.</h2>
              <p className="sec-lede">
                Thickness is read from the sheet, not the brochure. Every grade below is stocked in Blacktown with
                mill certificates on file.
              </p>
            </div>
            <ul className="mat-list">
              {(grades.length ? grades : [{ name: 'Loading…' }]).map(g => (
                <li key={g.name}>
                  <Link to="/materials" className="mat-row">
                    <span className="mat-name">
                      <b>{g.name}</b>
                      <small className="muted">{g.category}</small>
                    </span>
                    {g.min !== undefined && Number.isFinite(g.min) && (
                      <span className="mat-range mono">
                        {formatThickness(g.min)} – {formatThickness(g.max)}
                        <small>{g.count} sizes</small>
                      </span>
                    )}
                    <span className="mat-lead badge badge--ok">{g.lead || '—'}</span>
                    <Icon name="arrowRight" size={16} className="mat-arrow" />
                  </Link>
                </li>
              ))}
            </ul>
            <Link to="/materials" className="btn btn-s" style={{ marginTop: 22 }}>
              All materials
              <Icon name="arrowRight" />
            </Link>
          </div>
          <div className="home-mat-art iso-stage">
            <div className="iso-grid-bg" />
            <IsoIllustration scene="materials" />
            <div className="ih-hud ih-hud--br">3 · 7 · 12 · 20 mm</div>
          </div>
        </div>
      </section>

      {/* ── 고객 포털 ── */}
      <HomeGallery />

      <section className="sec sec--alt home-portal">
        <div className="wrap home-portal-grid">
          <div className="portal-mock card card--iso" aria-hidden="true">
            <div className="portal-mock-head">
              <span className="kicker kicker--plain">My page · orders</span>
              <span className="badge badge--spark"><span className="dot" />3 new</span>
            </div>
            <div className="portal-row">
              <span className="mono">TL-2025-0412</span>
              <span>SS304 2.0 · MS 5.0</span>
              <StatusBadge status="IN_PRODUCTION" />
            </div>
            <div className="portal-row">
              <span className="mono">TL-2025-0398</span>
              <span>AL5052 3.0</span>
              <StatusBadge status="SHIPPED" />
            </div>
            <div className="portal-row">
              <span className="mono">TL-2025-0385</span>
              <span>MS 10.0 · MS 6.0</span>
              <StatusBadge status="DELIVERED" />
            </div>
            <div className="portal-mock-foot">
              <span className="row" style={{ gap: 8 }}>
                <Icon name="shield" size={15} className="brand" />
                <span className="sm">MC-25-1187 · docket DD-25-4412</span>
              </span>
              <span className="btn btn-p btn-sm"><Icon name="download" />Download</span>
            </div>
          </div>

          <div>
            <div className="sec-head" style={{ marginBottom: 24 }}>
              <div className="kicker kicker--brand">Customer portal</div>
              <h2 className="sec-title">Everything about your order, in one account.</h2>
              <p className="sec-lede">
                No more emailing for a mill cert. Sign in to see quotes, order status, nesting utilisation and every
                document attached to a docket.
              </p>
            </div>
            <ul className="portal-features">
              {PORTAL_FEATURES.map(f => (
                <li key={f.t}>
                  <IsoGlyph name={f.glyph} size={46} />
                  <div>
                    <b>{f.t}</b>
                    <p>{f.d}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="row" style={{ gap: 10, marginTop: 26, flexWrap: 'wrap' }}>
              <Link to="/signup" className="btn btn-p">Create an account</Link>
              <Link to="/login" className="btn btn-g">I already have one</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="home-cta">
        <div className="iso-grid-bg iso-grid-bg--flat" />
        <div className="wrap home-cta-in">
          <div>
            <div className="kicker kicker--spark">Ready when you are</div>
            <h2>Upload a DXF. Get a number.<br />Confirmed within 24 hours.</h2>
            <p>No account needed for an indicative price. Sign in to save the quote and turn it into an order.</p>
          </div>
          <div className="home-cta-btns">
            <Link to="/quote" className="btn btn-spark btn-lg">
              <Icon name="upload" />
              Start a quote
            </Link>
            <Link to="/contact" className="btn btn-lg home-cta-ghost">
              Talk to an estimator
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
